import { useEffect, useRef, useState } from 'react';

import { CognitoError, PASSWORD_RULE, signInProblem } from '@going-once/client/cognito';

import {
  type PasswordResult,
  type Session,
  confirmAccount,
  createAccount,
  finishPasswordReset,
  googleClientId,
  onSignInRequest,
  resendCode,
  settleSignIn,
  signInWithGoogle,
  signInWithPassword,
  startPasswordReset,
} from '../lib/auth';
import { renderGoogleButton } from '../lib/google';
import { Field, Pressable, Sheet, Stack, Text } from '../ui/primitives';

/**
 * Sign-in, as our own sheet (D35 addendum, 2026-10-07) — the steps managed login used to
 * draw: sign in, create an account, confirm it with the emailed code, reset a forgotten
 * password, and the authenticator step for the few accounts with one.
 *
 * The phone's screen (`apps/mobile/src/app/sign-in.tsx`) step for step, so the two clients
 * behave alike. **One sheet with steps rather than five routes**, because the steps share
 * what the person has already typed: confirming an account signs you straight in with the
 * password you just chose rather than asking for it again.
 *
 * A sheet rather than a page so the person stays where they were — the auction they were
 * about to bid on — and that page reloads once they are in.
 */

type Step = 'sign-in' | 'create' | 'confirm' | 'forgot' | 'reset' | 'totp';

const TITLES: Record<Step, string> = {
  'sign-in': 'Sign in',
  create: 'Create an account',
  confirm: 'Check your email',
  forgot: 'Reset your password',
  reset: 'Choose a new password',
  totp: 'Authenticator code',
};

/** A problem this sheet found before asking Cognito, already in the words to show. */
class Missing extends Error {}

/** Refuse an empty field here: Cognito's answer to one names no field at all. */
function need(value: string, message: string): void {
  if (!value.trim()) throw new Missing(message);
}

function problem(error: unknown): string {
  if (error instanceof Missing) return error.message;
  const known = signInProblem(error);
  if (known) return known;
  console.error('sign-in failed', error);
  return 'Something went wrong. Try again.';
}

/**
 * Why a staff Google sign-in was refused, in one sentence for every reason. The pool does
 * not say which check failed (`apps/auth/src/verify-auth-challenge.ts`), and an unknown
 * account fails the same way on purpose.
 */
const GOOGLE_REFUSED = "That Google account can't sign in here. Staff use their CommentSold Google account.";

function googleProblem(error: unknown): string {
  if (error instanceof CognitoError && error.code !== 'TooManyRequestsException') return GOOGLE_REFUSED;
  return problem(error);
}

/**
 * Google's own button, drawn by Google's script into this element (GO-568). Shown only
 * where the environment configures staff Google sign-in.
 */
function GoogleButton({ clientId, onCredential }: { clientId: string; onCredential: (credential: string) => void }) {
  const parent = useRef<HTMLDivElement>(null);
  const latest = useRef(onCredential);
  latest.current = onCredential;
  const [failed, setFailed] = useState<string>();

  useEffect(() => {
    let stop: (() => void) | undefined;
    let live = true;
    if (parent.current) {
      renderGoogleButton(parent.current, clientId, (credential) => latest.current(credential))
        .then((done) => {
          if (live) stop = done;
          else done();
        })
        .catch((error: unknown) => {
          if (live) setFailed(error instanceof Error ? error.message : "Couldn't load Google sign-in.");
        });
    }
    return () => {
      live = false;
      stop?.();
    };
  }, [clientId]);

  return (
    <Stack gap="xs">
      <div ref={parent} />
      {failed ? <Text tone="danger">{failed}</Text> : null}
    </Stack>
  );
}

/** Rendered once by the shell; opens when a screen calls `signIn()`. */
export function SignInHost() {
  const [open, setOpen] = useState(false);
  useEffect(() => onSignInRequest(setOpen), []);
  if (!open) return null;
  return <SignInSheet />;
}

function SignInSheet() {
  const [step, setStep] = useState<Step>('sign-in');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [code, setCode] = useState('');
  const [error, setError] = useState<string>();
  const [notice, setNotice] = useState<string>();
  const [busy, setBusy] = useState(false);
  const totp = useRef<((code: string) => Promise<Session>) | undefined>(undefined);

  const go = (next: Step, message?: string) => {
    setStep(next);
    setCode('');
    setError(undefined);
    setNotice(message);
  };

  /** One press, one request, one place errors land. */
  const run = (work: () => Promise<void>) => async () => {
    setBusy(true);
    setError(undefined);
    try {
      await work();
    } catch (caught) {
      setError(problem(caught));
    } finally {
      setBusy(false);
    }
  };

  // A trailing space from autocomplete is the commonest reason an email "doesn't match".
  // Case needs nothing: the pool is case-insensitive.
  const address = email.trim();

  /**
   * In. The reload is what the managed-login redirect used to do, and why no screen has to
   * subscribe to the session: the page comes back on the same URL, restores the session
   * from the refresh token just stored, and renders against the new claims.
   */
  const signedIn = () => {
    settleSignIn(true);
    window.location.reload();
  };

  const finish = async (result: PasswordResult) => {
    if (result.status === 'signed-in') return signedIn();
    totp.current = result.submit;
    go('totp');
  };

  const signInNow = run(async () => {
    need(address, 'Enter your email.');
    need(password, 'Enter your password.');
    try {
      await finish(await signInWithPassword(address, password));
    } catch (caught) {
      if (!(caught instanceof CognitoError && caught.code === 'UserNotConfirmedException')) throw caught;
      // Signed up and never confirmed: send a fresh code rather than a dead end.
      await resendCode(address);
      go('confirm', `We sent a new code to ${address}.`);
    }
  });

  const create = run(async () => {
    need(address, 'Enter your email.');
    need(password, 'Choose a password.');
    const { confirmed } = await createAccount(address, password);
    // A test identity confirms itself (outside prod), so no code is coming.
    if (confirmed) return finish(await signInWithPassword(address, password));
    go('confirm', `We sent a code to ${address}. It can take a minute — check spam too.`);
  });

  const confirm = run(async () => {
    need(code, 'Enter the code from the email.');
    await confirmAccount(address, code.trim());
    await finish(await signInWithPassword(address, password));
  });

  const resend = run(async () => {
    await resendCode(address);
    setNotice(`We sent a new code to ${address}.`);
  });

  const forgot = run(async () => {
    need(address, 'Enter your email.');
    await startPasswordReset(address);
    // Said whether or not the account exists: the pool answers the same either way.
    go('reset', `If ${address} has an account, a code is on its way.`);
  });

  const reset = run(async () => {
    need(code, 'Enter the code from the email.');
    need(password, 'Choose a new password.');
    await finishPasswordReset(address, code.trim(), password);
    await finish(await signInWithPassword(address, password));
  });

  const google = googleClientId();
  const signInWithGoogleNow = (credential: string) =>
    void (async () => {
      setBusy(true);
      setError(undefined);
      try {
        await signInWithGoogle(credential);
        signedIn();
      } catch (caught) {
        setError(googleProblem(caught));
      } finally {
        setBusy(false);
      }
    })();

  const submitTotp = run(async () => {
    need(code, 'Enter the six-digit code.');
    if (!totp.current) return go('sign-in');
    await totp.current(code.trim());
    signedIn();
  });

  const emailField = (
    <Field id="sign-in-email" label="Email" value={email} onChange={setEmail} type="email" autoComplete="email" />
  );

  const passwordField = (fresh: boolean) => (
    <Field
      id="sign-in-password"
      label={step === 'reset' ? 'New password' : 'Password'}
      value={password}
      onChange={setPassword}
      type="password"
      autoComplete={fresh ? 'new-password' : 'current-password'}
      {...(fresh ? { note: PASSWORD_RULE } : {})}
    />
  );

  const codeField = (
    <Field
      id="sign-in-code"
      label={step === 'totp' ? 'Six-digit code' : 'Code'}
      value={code}
      onChange={setCode}
      autoComplete="one-time-code"
    />
  );

  const primary = (label: string, action: () => Promise<void>) => (
    <Pressable variant="primary" busy={busy} onPress={() => void action()}>
      {label}
    </Pressable>
  );
  const quiet = (label: string, action: () => void) => (
    <Pressable variant="quiet" disabled={busy} onPress={action}>
      {label}
    </Pressable>
  );

  return (
    <Sheet onDismiss={() => settleSignIn(false)}>
      <Stack gap="md">
        <Text step="heading">{TITLES[step]}</Text>
        {notice ? <Text tone="fg.muted">{notice}</Text> : null}

        {step === 'sign-in' ? (
          <>
            {google ? (
              <>
                <Text tone="fg.muted">Staff: sign in with your CommentSold Google account.</Text>
                <GoogleButton clientId={google} onCredential={signInWithGoogleNow} />
              </>
            ) : null}
            {emailField}
            {passwordField(false)}
            {primary('Sign in', signInNow)}
            {quiet('Create an account', () => go('create'))}
            {quiet('Forgot password?', () => go('forgot'))}
          </>
        ) : null}

        {step === 'create' ? (
          <>
            {emailField}
            {passwordField(true)}
            {primary('Create account', create)}
            {quiet('I already have an account', () => go('sign-in'))}
          </>
        ) : null}

        {step === 'confirm' ? (
          <>
            {codeField}
            {primary('Confirm', confirm)}
            {quiet('Send a new code', () => void resend())}
          </>
        ) : null}

        {step === 'forgot' ? (
          <>
            {emailField}
            {primary('Send a code', forgot)}
            {quiet('Back to sign in', () => go('sign-in'))}
          </>
        ) : null}

        {step === 'reset' ? (
          <>
            {codeField}
            {passwordField(true)}
            {primary('Save and sign in', reset)}
            {quiet('Send a new code', () => void forgot())}
          </>
        ) : null}

        {step === 'totp' ? (
          <>
            <Text tone="fg.muted">Enter the code from your authenticator app.</Text>
            {codeField}
            {primary('Verify', submitTotp)}
          </>
        ) : null}

        {error ? <Text tone="danger">{error}</Text> : null}
      </Stack>
    </Sheet>
  );
}
